import React, { memo, useCallback, useMemo } from 'react';
import classnames from 'clsx';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';

import { FlexColumn } from 'ui-components/components/flex';
import { GlobalEventListenerRoot } from 'ui-components/components/global-event-listener/root';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { useEnableNewHotkeySystem } from '../../../feature-flags/hooks/use-enable-new-hotkey-system';

import { DrawerProvider } from './context';
import style from './drawer.module.less';
import { CloseModalHotkey } from '../../../components/hotkeys/close-modal';
import { ConfirmDialogueModal } from '../../../components/modals/confirm-dialogue-modal';
import { useModal } from '../../../hooks/use-modal';
import { useModalManagerPortal } from '../../../portals/hooks';

/**
 * @typedef {import('./drawer-types.d.ts').DrawerType} DrawerType
 */

/**
 * @typedef {import('./drawer-types.d.ts').CancelHandler} CancelHandler
 */

/**
 * @typedef {import('./drawer-types.d.ts').CloseHandler} CloseHandler
 */

const DrawerContent = memo(({ onCancelCb, onClose, dataTestId, className, canvasClassName, children }) => (
  <>
    <div className={style['drawer-background']} data-testid={`${dataTestId}-background`} onClick={onCancelCb} />
    <DrawerProvider onCancel={onCancelCb} onClose={onClose}>
      <div className={classnames(style.drawer, className)} data-testid={dataTestId}>
        <FlexColumn className={classnames(canvasClassName, style.canvas)}>{children}</FlexColumn>
      </div>
    </DrawerProvider>
    <CloseModalHotkey onClose={onCancelCb} />
  </>
));

/**
 * @type {DrawerType}
 */
const Drawer = ({
  canvasClassName,
  className,
  onCancel,
  onClose,
  children,
  confirmCancelBodyMessage,
  confirmCancelButtonMessage,
  confirmCancelHeaderMessage,
  shouldNotUsePortal = false,
  dataTestId = 'drawer',
}) => {
  const isNewHotkeySystemEnabled = useEnableNewHotkeySystem();
  const zIndex = useMaxZ();
  const zIndexStyle = useMemo(() => ({ zIndex }), [zIndex]);
  const modalManagerPortalRef = useModalManagerPortal();

  const { open: onOpenConfirmModal, close: onCloseConfirmModal, isOpen: isConfirmModalOpen } = useModal();

  /**
   * @type {CancelHandler}
   */
  const onCancelCb = useCallback(() => {
    if (confirmCancelBodyMessage && confirmCancelButtonMessage && confirmCancelHeaderMessage) {
      onOpenConfirmModal();
    } else {
      onCancel();
    }
  }, [confirmCancelBodyMessage, confirmCancelButtonMessage, confirmCancelHeaderMessage, onCancel, onOpenConfirmModal]);

  const drawerContent = (
    <DrawerContent
      onCancelCb={onCancelCb}
      onClose={onClose}
      dataTestId={dataTestId}
      className={className}
      canvasClassName={canvasClassName}
    >
      {children}
    </DrawerContent>
  );

  const jsxToRender = (
    <div className={style.container} style={zIndexStyle}>
      {isNewHotkeySystemEnabled ? drawerContent : <GlobalEventListenerRoot>{drawerContent}</GlobalEventListenerRoot>}
      {isConfirmModalOpen ? (
        <ConfirmDialogueModal
          body={<FormattedMessage {...confirmCancelBodyMessage} />}
          buttonMessage={<FormattedMessage {...confirmCancelButtonMessage} />}
          header={<FormattedMessage {...confirmCancelHeaderMessage} />}
          onClose={onCloseConfirmModal}
          onConfirm={onCancel}
        />
      ) : null}
    </div>
  );

  if (shouldNotUsePortal) {
    return jsxToRender;
  }

  if (!modalManagerPortalRef) {
    return null;
  }

  return ReactDOM.createPortal(jsxToRender, modalManagerPortalRef);
};

export { Drawer };
